/**
 * Component Service - Fetch ship components (power plants, shields, coolers, etc.) from API
 * Used by LoadoutBuilder for component swapping
 */

import axios from '../api/axios';

// Component types that can be swapped in-game
export type SwappableComponentType = 
  | 'PowerPlant'
  | 'Shield'
  | 'Cooler'
  | 'QuantumDrive'
  | 'Radar'
  | 'Weapon'
  | 'WeaponGun'
  | 'WeaponMining'
  | 'Missile'
  | 'TractorBeam'
  | 'SalvageHead'
  | 'MiningModifier'
  | 'SalvageModifier';

// Full component data from API
export interface ComponentItem {
  id: number;
  itemName: string;
  localizedName: string;
  description: string;
  type: string;
  subType: string;
  size: number;
  grade: number;
  manufacturer: string;
  tags: string;
  requiredTags: string;
  powerBase: number;
  powerDraw: number;
  health: number;
  
  // Component-specific stats
  powerPlantStats?: {
    powerOutput: number | null;
  };
  shieldStats?: {
    shieldHealthMax: number | null;
    regenRate: number | null;
    downedRegenDelay: number | null;
    damagedRegenDelay: number | null;
  };
  coolerStats?: {
    coolingRate: number | null;
  };
  quantumDriveStats?: {
    driveSpeed: number | null;
    spoolUpTime: number | null;
    cooldownTime: number | null;
    fuelRequirement: number | null;
    calibrationTime: number | null;
  };
  radarStats?: {
    sensitivity: number | null;
    piercing: number | null;
  };
  weaponStats?: {
    fireRate: number | null;
    damagePerShot: number | null;
    damageType: string | null;
    range: number | null;
    velocity: number | null;
    ammoCapacity: number | null;
  };
  missileStats?: {
    damage: number | null;
    speed: number | null;
    lockTime: number | null;
    launchDelay: number | null;
    lockingRange: number | null;
  };
  miningLaserStats?: {
    instability: number | null;
    resistance: number | null;
    optimalWindow: number | null;
    power: number | null;
    range: number | null;
  };
  tractorBeamStats?: {
    range: number | null;
    force: number | null;
    volume: number | null;
    cargoRange: number | null;
    cargoForce: number | null;
  };
  salvageStats?: {
    headCount: number | null;
    armingDistance: number | null;
    usesCargoGrid: boolean | null;
  };
  
  // Power pip stats (4.5 Engineering Update)
  powerPipStats?: {
    powerUnits: number | null;
    minimumConsumptionFraction: number | null;
    minPipsRequired: number | null;
    powerRangeLowModifier: number | null;
    powerRangeMediumModifier: number | null;
    powerRangeHighModifier: number | null;
  };
  
  // Emission/Signature stats for detectability
  emissionStats?: EmissionStats;
}

// Emission/Signature data from EntityComponentPowerConnection and EntityComponentHeatConnection
export interface EmissionStats {
  // EM Signature fields
  powerToEM: number | null;          // EM signature per watt of power
  decayRateOfEM: number | null;      // How fast EM decays when power decreases
  emNominalSignature: number | null; // Base EM signature at full power
  
  // IR/Heat Signature fields
  thermalEnergyDraw: number | null;  // Heat generated at max power
  thermalEnergyBase: number | null;  // Base heat at idle
  temperatureToIR: number | null;    // Temp to IR conversion factor
  irNominalSignature: number | null; // Base IR signature
  
  // Temperature management
  maxCoolingRate: number | null;     // Max heat dissipation rate
  maxTemperature: number | null;     // Max safe operating temp
  overheatTemperature: number | null; // Overheat warning threshold
  startIRTemperature: number | null; // Temp where IR starts increasing
}

// Paginated response from API
export interface ComponentsResponse {
  type: string;
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  items: ComponentItem[];
}

// Query params for filtering components
export interface ComponentQueryParams {
  page?: number;
  pageSize?: number;
  size?: number;
  manufacturer?: string;
  search?: string;
  sortBy?: 'size' | 'name' | 'manufacturer' | 'grade';
  sortOrder?: 'asc' | 'desc';
}

class ComponentService {
  /**
   * Get components of a specific type with optional filtering
   */
  async getComponentsByType(
    type: SwappableComponentType,
    params: ComponentQueryParams = {}
  ): Promise<ComponentsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.manufacturer) queryParams.append('manufacturer', params.manufacturer);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = `/components/type/${type}${queryString ? `?${queryString}` : ''}`;
    const response = await axios.get<ComponentsResponse>(url);
    return response.data;
  }

  /**
   * Get a single component by ID
   */
  async getComponentById(id: number): Promise<ComponentItem> {
    const response = await axios.get<ComponentItem>(`/components/${id}`);
    return response.data;
  }

  /**
   * Get a component by its item name
   */
  async getComponentByName(itemName: string): Promise<ComponentItem> {
    const response = await axios.get<ComponentItem>(`/components/name/${encodeURIComponent(itemName)}`);
    return response.data;
  }

  /**
   * Search components across all types
   */
  async searchComponents(query: string, limit: number = 20): Promise<ComponentItem[]> {
    const response = await axios.get<{ query: string; count: number; results: ComponentItem[] }>(
      `/components/search?q=${encodeURIComponent(query)}&limit=${limit}`
    );
    return response.data.results;
  }

  /**
   * Get all available component types with counts
   */
  async getComponentTypes(): Promise<{ type: string; count: number }[]> {
    const response = await axios.get<{ type: string; count: number }[]>('/components/types');
    return response.data;
  }

  /**
   * Get all manufacturers with component counts
   */
  async getManufacturers(): Promise<{ manufacturer: string; count: number }[]> {
    const response = await axios.get<{ manufacturer: string; count: number }[]>('/components/manufacturers');
    return response.data;
  }

  /**
   * Convenience method: Get all power plants of a specific size
   */
  async getPowerPlantsBySize(size: number): Promise<ComponentItem[]> {
    const response = await this.getComponentsByType('PowerPlant', { 
      size, 
      pageSize: 100,
      sortBy: 'name',
      sortOrder: 'asc'
    });
    return response.items;
  }

  /**
   * Convenience method: Get all shields of a specific size
   */
  async getShieldsBySize(size: number): Promise<ComponentItem[]> {
    const response = await this.getComponentsByType('Shield', { 
      size, 
      pageSize: 100,
      sortBy: 'name',
      sortOrder: 'asc'
    });
    return response.items;
  }

  /**
   * Convenience method: Get all coolers of a specific size
   */
  async getCoolersBySize(size: number): Promise<ComponentItem[]> {
    const response = await this.getComponentsByType('Cooler', { 
      size, 
      pageSize: 100,
      sortBy: 'name',
      sortOrder: 'asc'
    });
    return response.items;
  }

  /**
   * Convenience method: Get all quantum drives of a specific size
   */
  async getQuantumDrivesBySize(size: number): Promise<ComponentItem[]> {
    const response = await this.getComponentsByType('QuantumDrive', { 
      size, 
      pageSize: 100,
      sortBy: 'name',
      sortOrder: 'asc'
    });
    return response.items;
  }

  /**
   * Convenience method: Get all radars of a specific size
   */
  async getRadarsBySize(size: number): Promise<ComponentItem[]> {
    const response = await this.getComponentsByType('Radar', { 
      size, 
      pageSize: 100,
      sortBy: 'name',
      sortOrder: 'asc'
    });
    return response.items;
  }

  /**
   * Convenience method: Get all tractor beams of a specific size
   */
  async getTractorBeamsBySize(size: number): Promise<ComponentItem[]> {
    const response = await this.getComponentsByType('TractorBeam', { 
      size, 
      pageSize: 100,
      sortBy: 'name',
      sortOrder: 'asc'
    });
    return response.items;
  }

  /**
   * Convenience method: Get all mining lasers of a specific size
   */
  async getMiningLasersBySize(size: number): Promise<ComponentItem[]> {
    const response = await this.getComponentsByType('WeaponMining', { 
      size, 
      pageSize: 100,
      sortBy: 'name',
      sortOrder: 'asc'
    });
    return response.items;
  }

  /**
   * Convenience method: Get all salvage heads of a specific size
   */
  async getSalvageHeadsBySize(size: number): Promise<ComponentItem[]> {
    const response = await this.getComponentsByType('SalvageHead', { 
      size, 
      pageSize: 100,
      sortBy: 'name',
      sortOrder: 'asc'
    });
    return response.items;
  }

  /**
   * Convenience method: Get all mining modules (gadgets that attach to mining lasers)
   * Size param is optional - mining modules are generally size-agnostic
   */
  async getMiningModules(size?: number): Promise<ComponentItem[]> {
    const response = await this.getComponentsByType('MiningModifier', { 
      size, 
      pageSize: 100,
      sortBy: 'name',
      sortOrder: 'asc'
    });
    return response.items;
  }

  /**
   * Convenience method: Get all salvage modules (gadgets that attach to salvage heads)
   */
  async getSalvageModules(size?: number): Promise<ComponentItem[]> {
    const response = await this.getComponentsByType('SalvageModifier', { 
      size, 
      pageSize: 100,
      sortBy: 'name',
      sortOrder: 'asc'
    });
    return response.items;
  }
}

export const componentService = new ComponentService();
export default componentService;
